import { Component, HostBinding, inject, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { PLATFORM_ID } from '@angular/core';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { UserService } from "./services/user.service";
import { AuthService } from "./services/auth.service";
import { LoginEndconsumerResponseData } from "./models/user.model";
import { ActivatedRoute, NavigationCancel, NavigationEnd, NavigationError, Router, RouterStateSnapshot, ChildActivationEnd } from "@angular/router";
import { NgxSmartModalService } from "ngx-smart-modal";
import { LanguageService } from "./services/language.service";
import { AppTitleService } from "./services/title.service";
import { TranslateService } from "@ngx-translate/core";
import { Meta } from '@angular/platform-browser';
import { Subscription, filter, take, startWith, map } from "rxjs";

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss'
})
export class AppComponent implements OnInit, OnDestroy {
  private _userService: UserService = inject(UserService);
  private _authService: AuthService = inject(AuthService);
  private _activatedRoute: ActivatedRoute = inject(ActivatedRoute);
  private _router: Router = inject(Router);
  private _ngxSmartModalService: NgxSmartModalService = inject(NgxSmartModalService);
  private _languageService : LanguageService = inject(LanguageService);
  private _renderer: Renderer2 = inject(Renderer2);
  private _appTitleService: AppTitleService = inject(AppTitleService);
  private _translate: TranslateService = inject(TranslateService);
  private _meta: Meta = inject(Meta);
  private _platformId = inject(PLATFORM_ID);
  private _document: Document = inject(DOCUMENT);

  preloaderInDom = true;
  preloaderVisible = true;
  @HostBinding('class.app-content-visible') contentVisible = false;
  langSubscription!: Subscription;
  private readonly _MIN_PRELOADER_VISIBLE_MS = 800;
  private readonly _PRELOADER_DISMOUNT_DELAY_MS = 500;
  private readonly _PRELOADER_FALLBACK_TIMEOUT_MS = 3000;

  private _preloaderVisibleAt = Date.now();
  private _preloaderHideTimeoutId: any = null;
  private _preloaderRemoveTimeoutId: any = null;
  private _preloaderFallbackTimeoutId: any = null;
  private _preloaderNavigationSub?: Subscription;

  ngOnInit(): void {
    this.initializePreloader();
    // this._languageService.initLanguageFromUrlOrBrowser();
    this.languageSubscription();
    this.handleRouterTitleUpdates();

    this._activatedRoute.queryParams.subscribe((params) => {
      const token: string = params['token'];
      const email: string | null = params['email'] ? decodeURIComponent(params['email']) : null;
      const userType: 'end_consumer'|'team' = params['user_type'];

      if (token && email) {
        if (userType === 'team') {
          this._router.navigate(['/henter-pant']).then(() => {
            this._ngxSmartModalService.getModal('changePasswordModal').open();
            this._ngxSmartModalService.setModalData({token, email}, 'changePasswordModal', true);
          });
        } else if (userType === 'end_consumer') {
          this._ngxSmartModalService.getModal('changePasswordModal').open();
          this._ngxSmartModalService.setModalData({token, email}, 'changePasswordModal', true);
          this.clearParams();
        } else {
          console.error('AppComponent#reset password "user-type" not recognised: ', userType);
        }
      }
    });

    this.setToken();
  }

  languageSubscription(): void {
    this.langSubscription = this._languageService.onLangChange$.subscribe((event: any) => {
      this.updateBodyClass(this._languageService.currentLang);
      this.updateTitleFromCurrentRoute();
      this.updateSocialPreviewFromCurrentRoute();
    });
  }

  updateBodyClass(lang: string) {
    const body = this._document.body;
    if (!body) return;

    body.classList.forEach(cls => {
      if (cls.startsWith('lang-')) {
        this._renderer.removeClass(body, cls);
      }
    });
    this._renderer.addClass(body, `lang-${lang}`);
  }

  private handleRouterTitleUpdates(): void {
    this._router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.updateTitleFromCurrentRoute();
        this.updateSocialPreviewFromCurrentRoute();
      }
    });
  }

  private updateTitleFromCurrentRoute(): void {
    let route = this._activatedRoute;
    while (route.firstChild) {
      route = route.firstChild;
    }
    const titleKey: string | undefined = route.snapshot.data && route.snapshot.data['titleKey'];
    const isHenterPant = this._router.url.includes('/henter-pant');
    if (titleKey) {
      this._translate.get(titleKey).subscribe((baseTitle: string) => {
        const brandKey = isHenterPant ? 'PAGE_TITLE.FORMAT_HP' : 'PAGE_TITLE.FORMAT';
        this._appTitleService.setTitleFromKey(brandKey, { title: baseTitle });
      });
    } else {
      this._appTitleService.setTitleFromKey(isHenterPant ? 'PAGE_TITLE.DEFAULT_HP' : 'PAGE_TITLE.DEFAULT');
    }
  }

  private updateSocialPreviewFromCurrentRoute(): void {
    let route = this._activatedRoute;
    while (route.firstChild) {
      route = route.firstChild;
    }
    const isHenterPant = this._router.url.includes('/henter-pant');
    const ogKey: string | undefined = route.snapshot.data && route.snapshot.data['ogKey'];

    const ogMap: Record<string, string> = {
      HOME: '/assets/images/previews/gp-preview.png',
      PRIVACY: '/assets/images/previews/gp-preview.png',
      TERMS: '/assets/images/previews/gp-preview.png',
      FAQ: '/assets/images/previews/gp-preview.png',
      HENTERPANT: '/assets/images/previews/hp-preview.png'
    };

    let imageUrl = '';
    if (isHenterPant) {
      imageUrl = ogMap['HENTERPANT'];
    } else if (ogKey && ogMap[ogKey]) {
      imageUrl = ogMap[ogKey];
    } else {
      imageUrl = ogMap['HOME'];
    }

    this._meta.updateTag({ property: 'og:image', content: imageUrl });
    this._meta.updateTag({ name: 'twitter:image', content: imageUrl });
  }

  setToken(): void {
    const userData: LoginEndconsumerResponseData | '' = this._userService.get();

    if (userData && userData.tokens) {
      this._authService.setAccessToken(userData.tokens.access);
    }
  }

  clearParams(): void {
    setTimeout(() => {
      this._router.navigate(['/'], {
        replaceUrl: true,
      });
    }, 100);
  }

  ngOnDestroy(): void {
    if (this.langSubscription) this.langSubscription.unsubscribe();
    if (this._preloaderNavigationSub) this._preloaderNavigationSub.unsubscribe();
    if (this._preloaderHideTimeoutId) clearTimeout(this._preloaderHideTimeoutId);
    if (this._preloaderRemoveTimeoutId) clearTimeout(this._preloaderRemoveTimeoutId);
    if (this._preloaderFallbackTimeoutId) clearTimeout(this._preloaderFallbackTimeoutId);
    this.removePreloaderLock();
  }

  private initializePreloader(): void {
    if (!isPlatformBrowser(this._platformId)) {
      this.addPreloaderLock();
      return;
    }

    this._preloaderVisibleAt = Date.now();
    this.addPreloaderLock();

    this._preloaderNavigationSub = this._router.events
      .pipe(
        filter((event) => 
          event instanceof NavigationEnd || 
          event instanceof NavigationCancel || 
          event instanceof NavigationError
        ),
        take(1)
      )
      .subscribe(() => {
        setTimeout(() => this.hidePreloader(), 200);
      });

    if (this._router.navigated) {
      setTimeout(() => this.hidePreloader(), 600);
    }

    this._preloaderFallbackTimeoutId = setTimeout(() => {
      this.hidePreloader();
    }, this._PRELOADER_FALLBACK_TIMEOUT_MS);
  }

  private hidePreloader(): void {
    if (!isPlatformBrowser(this._platformId) || !this.preloaderInDom) {
      return;
    }

    const elapsed = Date.now() - this._preloaderVisibleAt;
    const remaining = Math.max(0, this._MIN_PRELOADER_VISIBLE_MS - elapsed);

    if (this._preloaderHideTimeoutId) clearTimeout(this._preloaderHideTimeoutId);
    if (this._preloaderFallbackTimeoutId) clearTimeout(this._preloaderFallbackTimeoutId);

    this._preloaderHideTimeoutId = setTimeout(() => {
      this.preloaderVisible = false;
      this.contentVisible = true;
      this.removePreloaderLock();

      if (this._preloaderRemoveTimeoutId) clearTimeout(this._preloaderRemoveTimeoutId);
      this._preloaderRemoveTimeoutId = setTimeout(() => {
        this.preloaderInDom = false;
        this._preloaderRemoveTimeoutId = null;
      }, this._PRELOADER_DISMOUNT_DELAY_MS);
    }, remaining);
  }

  private addPreloaderLock(): void {
    const body = this._document.body;
    if (body) {
      this._renderer.addClass(body, 'preloader-active');
    }
  }

  private removePreloaderLock(): void {
    const body = this._document.body;
    if (body) {
      this._renderer.removeClass(body, 'preloader-active');
    }
  }
}
